<template>
  <div id="investment-memo-form" class="tw-relative">
    <KycDropdown
      title="Investment Memo"
      subTitle="Add your investment memo and supporting documents"
      :status="orgDetails.tokens?.length ? true : false"
      :edit="''"
      @active-dropdown="setDropdownStatus"
    >
      <template v-slot:content>
        <div class="memo-form-container">
          <form
            @submit.prevent="uploadDocuments"
            class="memo-form"
          >
            <div class="memo-section">
              <label class="memo-label">Investment Memo</label>
              <p class="memo-hint">Describe your company, the problem you're solving, your solution, market opportunity, traction, and team.</p>
              <WysiwygEditor
                @update:modelValue="storeMemoValue"
                class="memo-editor"
                placeholder="Write your investment memo here..."
                name="memo"
                :content="memoPayload.memo"
              />
            </div>

            <div class="documents-section">
              <label class="memo-label">Supporting Documents</label>
              <p class="memo-hint">Upload any relevant documents (pitch deck, financials, etc.)</p>
              
              <div class="documents-grid">
                <div
                  class="document-item"
                  v-for="(document, index) in memoPayload.documents"
                  :key="'doc-' + index"
                >
                  <TextInput
                    class="document-name-input"
                    placeHolder="Document name"
                    label=""
                    type="text"
                    fieldType="text"
                    id="document_name"
                    name="document_name"
                    :showLabel="false"
                    :text="document.name"
                    @set="setDocumentType($event, index)"
                    @valid="documentNameValid"
                  />
                  <DragNDrop
                    accept=".pdf"
                    :currentValue="memoPayload.documents[index].url"
                    :startUpload="uploadDocument[index].doc"
                    :hasMaxWidth="false"
                    @file-added="setDocument($event, index)"
                    @reupload="uploadDocument[index].doc = false"
                    @upload-complete="submitMemo($event, index)"
                    @uploadError="uploadDocumentError(index)"
                  >
                  </DragNDrop>
                  <button
                    v-if="memoPayload.documents.length > 1"
                    type="button"
                    class="remove-doc-btn"
                    @click="removeDocument(document, index)"
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M2 4H3.33333H14" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
                      <path d="M5.33333 4V2.66667C5.33333 2.31305 5.47381 1.97391 5.72386 1.72386C5.97391 1.47381 6.31304 1.33334 6.66667 1.33334H9.33333C9.68696 1.33334 10.0261 1.47381 10.2761 1.72386C10.5262 1.97391 10.6667 2.31305 10.6667 2.66667V4M12.6667 4V13.3333C12.6667 13.687 12.5262 14.0261 12.2761 14.2761C12.0261 14.5262 11.687 14.6667 11.3333 14.6667H4.66667C4.31305 14.6667 3.97391 14.5262 3.72386 14.2761C3.47381 14.0261 3.33333 13.687 3.33333 13.3333V4H12.6667Z" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    <span>Remove</span>
                  </button>
                </div>
              </div>

              <button
                type="button"
                class="add-doc-btn"
                @click="addDocument"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8 3.33334V12.6667" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M3.33333 8H12.6667" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <span>Add Document</span>
              </button>
            </div>

            <div class="form-actions">
              <Btn
                :loading="memoLoading"
                :title="$route.name === 'EsopDetails' ? 'Save Changes' : 'Continue'"
              />
            </div>
          </form>
        </div>
      </template>
    </KycDropdown>
  </div>
</template>

<script>
import Btn from "@/components/general/BtnComponent.vue";
import DragNDrop from "@/components/general/DragNDrop.vue";
import KycDropdown from "@/components/general/KycDropdown.vue";
import TextInput from "@/components/general/TextInput.vue";
import WysiwygEditor from "@/components/general/WysiwygEditor";
import { mapActions, mapGetters, mapState } from "vuex";

export default {
  name: "InvestmentMemoForm",

  components: {
    KycDropdown,
    TextInput,
    DragNDrop,
    WysiwygEditor,
    Btn,
  },

  data() {
    return {
      count: 0,
      debounce: null,
      memoPayload: {
        documents: [{ type: "", name: "", url: "" }],
        memo: "",
      },
      memoLoading: false,
      uploadDocument: [{ doc: false }],
    };
  },

  computed: {
    ...mapGetters("organisationModule", ["lastRaiseStage"]),
    ...mapState({
      orgDetails: (state) => state.organisationModule.organisationDetails,
      assetDetails: (state) => state.assetModule.assetDetails,
    }),
  },

  mounted() {
    this.updateTokenInfo();
  },

  methods: {
    ...mapActions("assetModule", [
      "getAsset",
      "updateAsset",
      "deleteDocument",
    ]),
    ...mapActions(["showToast"]),

    setDropdownStatus(title) {
      this.$emit("dropdown-title", title);
    },

    storeMemoValue(val) {
      if (val.value === "<p></p>") {
        this.memoPayload.memo = "";
      } else {
        this.memoPayload.memo = val.value;
      }
    },

    async submitMemo(url, index) {
      this.memoPayload.documents.map((document, docIndex) => {
        if (index === docIndex) {
          document.url = url;
        }
      });
      this.count++;
      if (this.memoPayload.documents.length === this.count) {
        const payload = {
          about: this.assetDetails.about,
          name: this.assetDetails.name,
          type: this.assetDetails.type,
          company_type: this.assetDetails.company_type,
          investment_memo: this.memoPayload,
        };
        const response = await this.updateAsset({
          data: payload,
          id: this.$route.query.asset
            ? this.$route.query.asset
            : this.assetDetails._id,
        });
        if (response) this.$emit("dropdown-title", "Location");
        this.uploadDocument.map((document, index) => {
          this.uploadDocument[index].doc = false;
        });
        this.count = 0;
        this.memoLoading = false;
      }
    },

    uploadDocuments() {
      setTimeout(() => {
        if (!this.memoPayload.memo) {
          return this.showToast({
            description: "Please fill in the Investment Memo",
            display: true,
            type: "error",
          });
        }
        this.uploadDocument.map((document, index) => {
          this.uploadDocument[index].doc = true;
          this.memoLoading = true;
        });
      }, 500);
    },

    addDocument() {
      this.memoPayload.documents.push({ type: "", name: "", url: "" });
      this.uploadDocument.push({ doc: false });
    },

    removeDocument(document, index) {
      this.memoPayload.documents.splice(index, 1);
      this.uploadDocument.splice(index, 1);
      if (!document.url) return;
      const payload = { docId: document._id, assetId: this.assetDetails._id };
      this.deleteDocument(payload);
    },

    setDocument(val, index) {
      this.memoPayload.documents[index].url = val;
    },

    setDocumentType(text, index) {
      this.memoPayload.documents[index].name = text;
      this.memoPayload.documents[index].type = "Other";
    },

    documentNameValid(valid) {
      // validation handler
    },

    uploadDocumentError(index) {
      this.memoLoading = false;
      this.uploadDocument[index].doc = false;
    },

    updateTokenInfo() {
      if (this.assetDetails.documents?.length) {
        let newDocuments = [];
        let newUploadDocument = [];
        this.assetDetails.documents.map((document, index) => {
          if (
            document &&
            document.type !== "Incorporation" &&
            document.type !== "Identification"
          ) {
            let obj = {
              type: document?.type,
              name: document?.name,
              url: document?.url,
              _id: document?._id,
            };
            newDocuments.push(obj);
            newUploadDocument.push({ doc: false });
          }
        });
        if (newDocuments.length) {
          this.uploadDocument = newUploadDocument;
          this.memoPayload.documents = newDocuments.reverse();
        }
      }
      this.memoPayload.memo = this.assetDetails.memo?.memo || '';
    },
  },

  watch: {
    assetDetails(newValue, oldValue) {
      deep: true;
      immediate: true;
      if (newValue !== oldValue) {
        this.updateTokenInfo();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.memo-form-container {
  padding: 0 24px;

  @media screen and (min-width: 768px) {
    padding: 0 48px;
  }
}

.memo-form {
  width: 100%;
  padding: 32px 0;
}

.memo-section {
  margin-bottom: 32px;
}

.memo-label {
  display: block;
  font-family: 'DM Sans', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #0F172A;
  margin-bottom: 6px;
}

.memo-hint {
  font-family: 'DM Sans', sans-serif;
  font-size: 13px;
  color: #64748B;
  margin: 0 0 12px 0;
  line-height: 1.5;
}

.memo-editor {
  width: 100%;
  min-height: 280px;
}

.documents-section {
  margin-bottom: 32px;
}

.documents-grid {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 16px;
}

.document-item {
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
  border-radius: 10px;
  padding: 16px;
}

.document-name-input {
  margin-bottom: 12px;
}

.remove-doc-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 12px;
  padding: 8px 12px;
  background: none;
  border: none;
  color: #EF4444;
  font-family: 'DM Sans', sans-serif;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 6px;

  &:hover {
    background: #FEF2F2;
  }
}

.add-doc-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: #F8FAFC;
  border: 1px dashed #CBD5E1;
  border-radius: 8px;
  color: #4D84FF;
  font-family: 'DM Sans', sans-serif;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #EEF2FF;
    border-color: #4D84FF;
  }
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  padding-top: 16px;
  border-top: 1px solid #E2E8F0;
  margin-top: 24px;
}
</style>
